import { useCallback, useMemo } from 'react';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import { createHook } from '@atlassian/react-sweet-state';
import { useQueryParam } from '@atlassian/react-resource-router';
import { useCurrentPreviewPanel, usePreviewPanelsActions } from '@atlassian/preview-panels-api';
import { fg } from '@atlassian/jira-feature-gating';

import type { RightSidebar } from '../../../../common/types.tsx';
import { selectCurrentRightSidebar } from '../../actions/right-sidebar/index.tsx';
import { layoutStore, useLayoutStoreActions } from '../../store/index.tsx';

const useActiveRightSidebar = createHook(layoutStore, {
	selector: (state) => {
		const rightSidebarState = selectCurrentRightSidebar(state.content.rightSidebar);

		if (!rightSidebarState) return null;

		return {
			...rightSidebarState,
			shouldRender: !state.isChromeless,
		};
	},
});

type ExtendedRightSidebarState = {
	shouldRender: boolean;
} & RightSidebar;

export const useActiveRightSidebarState = (): ExtendedRightSidebarState | null => {
	const [rightSidebar] = useActiveRightSidebar();
	return rightSidebar;
};

export type UseRightSidebarStateType = typeof useActiveRightSidebarState;

const useRightSidebar = createHook(layoutStore, {
	selector: (state) => state.content.rightSidebar,
});

export const useRightSidebarState = () => {
	const [rightSidebarState] = useRightSidebar();
	return rightSidebarState;
};

export const useRightSidebarController = () => {
	const [
		,
		{
			setRightSidebarState,
			openRightSidebar: openRightSidebarAction,
			closeRightSidebar,
			closeCurrentRightSidebar,
			closeAllRightSidebar,
			minimiseRightSidebar,
		},
	] = useLayoutStoreActions();

	let closePreviewPanel: (() => void) | undefined;
	let isIssuePreviewPanelOpen = false;
	let setSelectedIssue: ((value: string | undefined) => void) | undefined;

	// eslint-disable-next-line jira/ff/no-preconditioning
	if (fg('jira-glance-panel') && fg('jira_preview_panel_m_1_rollout')) {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const [, setSelectedIssueHook] = useQueryParam('selectedIssue');
		setSelectedIssue = setSelectedIssueHook;
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const { close } = usePreviewPanelsActions();
		closePreviewPanel = close;
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const panel = useCurrentPreviewPanel();
		isIssuePreviewPanelOpen = !!(panel !== null && panel.type === 'jira-issue');
	}

	const openRightSidebar =
		// eslint-disable-next-line jira/ff/no-preconditioning
		fg('right-sidebar-preview-panel') &&
		fg('jira-glance-panel') &&
		fg('jira_preview_panel_m_1_rollout')
			? // eslint-disable-next-line react-hooks/react-compiler
				// eslint-disable-next-line react-hooks/rules-of-hooks
				useCallback(
					(panelId: string, width: number, isMinimised = false, toTop = false) => {
						if (
							isIssuePreviewPanelOpen &&
							panelId !== 'jsw-nux-onboarding-checklist' &&
							fg('issue_view_gpp_killswitch')
						) {
							openRightSidebarAction(panelId, width, isMinimised, toTop);
							setSelectedIssue?.(undefined);
							closePreviewPanel?.();
						} else {
							openRightSidebarAction(panelId, width, isMinimised, toTop);
							closePreviewPanel?.();
						}
					},
					[closePreviewPanel, isIssuePreviewPanelOpen, openRightSidebarAction, setSelectedIssue],
				)
			: openRightSidebarAction;

	return useMemo(
		() => ({
			setRightSidebarState,
			closeRightSidebar,
			openRightSidebar,
			closeCurrentRightSidebar,
			closeAllRightSidebar,
			minimiseRightSidebar,
		}),
		[
			closeAllRightSidebar,
			closeCurrentRightSidebar,
			closeRightSidebar,
			openRightSidebar,
			setRightSidebarState,
			minimiseRightSidebar,
		],
	);
};

export type UseRightSidebarControllerType = typeof useRightSidebarController;
